/**
 * Formatter Utilities
 * Token estimation, text processing, and timing utilities
 */

/**
 * Simple timer for measuring operation duration
 */
export interface SimpleTimer {
  /** Get elapsed time in milliseconds */
  elapsed(): number;
}

/**
 * Start a simple timer for measuring operation duration
 * Unlike logger.startTimer(), this doesn't log automatically
 *
 * @returns Timer object with elapsed() method
 *
 * @example
 * ```typescript
 * const timer = startTimer();
 * await doSomething();
 * const duration_ms = timer.elapsed();
 * ```
 */
export function startTimer(): SimpleTimer {
  const start = Date.now();
  return {
    elapsed: () => Date.now() - start,
  };
}

/**
 * Estimate tokens for text using a calibrated heuristic
 *
 * Rule of thumb: ~4.5 characters per token for technical/code text
 * Calibrated against actual GPT-4 tokenization (12.9% -> ~3-5% error)
 *
 * @param text - The text to estimate tokens for
 * @returns Estimated token count
 */
export function estimateTokensForText(text: string): number {
  // Remove extra whitespace
  const normalized = text.trim().replace(/\s+/g, ' ');

  // Handle empty string
  if (normalized.length === 0) {
    return 0;
  }

  // Calibrated estimates for technical/code content
  // 4.5 chars/token is more accurate than the standard 4 chars/token
  const charBasedEstimate = Math.ceil(normalized.length / 4.5);

  // Word-based estimate (fallback for text-heavy content)
  const words = normalized.split(/\s+/).length;
  const wordBasedEstimate = Math.ceil(words * 1.25); // ~1.25 tokens per word

  // Use the higher estimate (slightly conservative)
  return Math.max(charBasedEstimate, wordBasedEstimate);
}

/**
 * Truncate text to fit within a token budget
 *
 * @param text - The text to truncate
 * @param tokenBudget - Maximum number of tokens
 * @returns Truncated text
 */
export function truncateToTokenBudget(text: string, tokenBudget: number): string {
  const currentTokens = estimateTokensForText(text);

  if (currentTokens <= tokenBudget) {
    return text;
  }

  // Calculate target character count (conservative)
  const targetChars = tokenBudget * 4;

  // Truncate and add ellipsis
  const truncated = text.slice(0, targetChars - 3);
  return `${truncated}...`;
}

/**
 * Estimate tokens for JSON object
 *
 * @param obj - The object to estimate tokens for
 * @returns Estimated token count
 */
export function estimateTokensForJSON(obj: unknown): number {
  const jsonString = JSON.stringify(obj);
  return estimateTokensForText(jsonString);
}
